import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Container, Card, Table, Row, Col, Form, Button, Pagination, Spinner, Alert, Modal, Badge } from "react-bootstrap";
import axios from "axios";
import { BsEye } from "react-icons/bs";
import { FaTrash } from "react-icons/fa";
import { URL } from "../../../config";
import "./AdminNotifications.css";

const DEFAULT_LIMIT = 10;

const AdminNotifications = () => {
  const token = localStorage.getItem("adminToken");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: DEFAULT_LIMIT, total: 0, totalPages: 1 });
  const [summary, setSummary] = useState({ totalAll: 0, unreadAll: 0, totalResults: 0 });

  // filters
  const [recipientType, setRecipientType] = useState("");
  const [recipientId, setRecipientId] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [type, setType] = useState("");
  const [isRead, setIsRead] = useState(""); // "" | "true" | "false"
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // view modal
  const [showView, setShowView] = useState(false);
  const [selected, setSelected] = useState(null);

  // delete modal
  const [showDelete, setShowDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : undefined),
    [token]
  );

  const formatDateTime = (iso) => {
    if (!iso) return "N/A";
    const d = new Date(iso);
    return d.toLocaleString("en-BD", { dateStyle: "medium", timeStyle: "short" });
  };

  const getPageItems = (total, current, maxButtons = 7) => {
    const pages = [];
    pages.push(1);
    const siblings = Math.floor((maxButtons - 3) / 2);
    let start = Math.max(2, current - siblings);
    let end = Math.min(total - 1, current + siblings);
    if (current <= siblings + 2) end = Math.min(total - 1, maxButtons - 1);
    if (current >= total - (siblings + 1)) start = Math.max(2, total - (maxButtons - 2));
    if (start > 2) pages.push("start-ellipsis");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total - 1) pages.push("end-ellipsis");
    if (total > 1) pages.push(total);
    return pages;
  };

  const fetchData = useCallback(
    async (pageOverride) => {
      setLoading(true);
      setError("");
      try {
        const page = pageOverride ?? meta.page;

        const { data } = await axios.get(`${URL}/admin/notifications`, {
          headers,
          params: {
            page,
            limit: meta.limit,
            recipientType: recipientType || undefined,
            recipientId: recipientId || undefined,
            orderNumber: orderNumber || undefined,
            type: type || undefined,
            isRead: isRead || undefined,
            search: search || undefined,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
          },
        });

        setRows(Array.isArray(data?.data) ? data.data : []);
        setMeta({
          page: data?.meta?.page || page,
          limit: data?.meta?.limit || meta.limit,
          total: data?.meta?.total || 0,
          totalPages: data?.meta?.totalPages || 1,
        });
        setSummary({
          totalAll: Number(data?.summary?.totalAll || 0),
          unreadAll: Number(data?.summary?.unreadAll || 0),
          totalResults: Number(data?.summary?.totalResults || 0),
        });
      } catch (err) {
        console.error(err);
        setError(err?.response?.data?.error || "Failed to load notifications.");
        setRows([]);
        setSummary({ totalAll: 0, unreadAll: 0, totalResults: 0 });
      } finally {
        setLoading(false);
      }
    },
    [headers, meta.page, meta.limit, recipientType, recipientId, orderNumber, type, isRead, search, startDate, endDate]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSearch = (e) => {
    e.preventDefault();
    setMeta((m) => ({ ...m, page: 1 }));
    fetchData(1);
  };

  const onReset = () => {
    setRecipientType("");
    setRecipientId("");
    setOrderNumber("");
    setType("");
    setIsRead("");
    setSearch("");
    setStartDate("");
    setEndDate("");
    setMeta((m) => ({ ...m, page: 1 }));
    fetchData(1);
  };

  const handlePageChange = (p) => {
    setMeta((m) => ({ ...m, page: p }));
    fetchData(p);
  };

  const openView = (row) => {
    setSelected(row);
    setShowView(true);
  };
  const closeView = () => {
    setSelected(null);
    setShowView(false);
  };

  const openDelete = (row) => {
    setError("");
    setDeleteTarget(row);
    setShowDelete(true);
  };
  const closeDelete = () => {
    setDeleteTarget(null);
    setShowDelete(false);
  };

  const confirmDelete = async () => {
    const id = deleteTarget?.Id;
    if (!id) return;

    try {
      setDeleting(true);
      setError("");

      await axios.delete(`${URL}/admin/notifications/${id}`, { headers });

      const willBeEmpty = rows.length === 1;
      const nextPage = willBeEmpty && meta.page > 1 ? meta.page - 1 : meta.page;

      closeDelete();
      setMeta((m) => ({ ...m, page: nextPage }));
      fetchData(nextPage);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || "Failed to delete notification.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container fluid className="adminNotif-container">
      <Card className="adminNotif-card">
        <Card.Header className="adminNotif-header">
          <h4 className="adminNotif-title">Notifications</h4>
        </Card.Header>

        <Card.Body>
          {/* Summary */}
          <Row className="g-3 mb-3">
            <Col md={4}>
              <div className="adminNotif-stat">
                <div className="adminNotif-stat-label">TOTAL (ALL)</div>
                <div className="adminNotif-stat-value">{summary.totalAll}</div>
              </div>
            </Col>
            <Col md={4}>
              <div className="adminNotif-stat">
                <div className="adminNotif-stat-label">UNREAD (ALL)</div>
                <div className="adminNotif-stat-value">{summary.unreadAll}</div>
              </div>
            </Col>
            <Col md={4}>
              <div className="adminNotif-stat">
                <div className="adminNotif-stat-label">RESULTS (FILTERED)</div>
                <div className="adminNotif-stat-value">{summary.totalResults}</div>
              </div>
            </Col>
          </Row>

          {/* Filters */}
          <Form onSubmit={onSearch} className="mb-3">
            <Row className="g-3 align-items-end">
              <Col md={2}>
                <Form.Label>Recipient Type</Form.Label>
                <Form.Select value={recipientType} onChange={(e) => setRecipientType(e.target.value)}>
                  <option value="">All</option>
                  <option value="user">User</option>
                  <option value="servitor">Servitor</option>
                </Form.Select>
              </Col>

              <Col md={2}>
                <Form.Label>Recipient ID</Form.Label>
                <Form.Control
                  value={recipientId}
                  onChange={(e) => setRecipientId(e.target.value)}
                  placeholder="e.g. 12"
                />
              </Col>

              <Col md={2}>
                <Form.Label>Order Number</Form.Label>
                <Form.Control
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="ES-1001"
                />
              </Col>

              <Col md={2}>
                <Form.Label>Type</Form.Label>
                <Form.Control
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  placeholder="order_accepted"
                />
              </Col>

              <Col md={2}>
                <Form.Label>Read Status</Form.Label>
                <Form.Select value={isRead} onChange={(e) => setIsRead(e.target.value)}>
                  <option value="">All</option>
                  <option value="false">Unread</option>
                  <option value="true">Read</option>
                </Form.Select>
              </Col>

              <Col md={2}>
                <Form.Label>Search</Form.Label>
                <Form.Control
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="message / type"
                />
              </Col>

              <Col md={2}>
                <Form.Label>Start Date</Form.Label>
                <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Col>

              <Col md={2}>
                <Form.Label>End Date</Form.Label>
                <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Col>

              <Col md={8} className="text-end">
                <Button type="submit" className="me-2">Search</Button>
                <Button variant="outline-primary" onClick={onReset}>Reset</Button>
              </Col>
            </Row>
          </Form>

          {loading && (
            <div className="text-center my-4">
              <Spinner animation="border" />
            </div>
          )}

          {error && (
            <Alert variant="danger" className="mt-2">
              {error}
            </Alert>
          )}

          {!loading && !error && rows.length === 0 && (
            <p className="text-muted">No notifications found.</p>
          )}

          {!loading && !error && rows.length > 0 && (
            <>
              <Table striped bordered hover responsive className="adminNotif-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Recipient</th>
                    <th>Order</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th style={{ width: 120 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={r.Id}>
                      <td>{idx + 1 + (meta.page - 1) * meta.limit}</td>
                      <td>
                        <div>
                          <b>{r.recipientType}</b> — ID: {r.recipientId}
                        </div>
                      </td>
                      <td>
                        {r.orderNumber ? (
                          <span>{r.orderNumber}</span>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td>{r.type || "N/A"}</td>
                      <td>
                        {r.isRead ? <Badge bg="secondary">Read</Badge> : <Badge bg="warning">Unread</Badge>}
                      </td>
                      <td>{formatDateTime(r.created_at)}</td>
                      <td className="adminNotif-actions-td">
  <div className="adminNotif-actions">
    <Button
      variant="outline-primary"
      size="sm"
      onClick={() => openView(r)}
      title="View"
      className="adminNotif-actionBtn"
    >
      <BsEye />
    </Button>

    <Button
      variant="outline-danger"
      size="sm"
      onClick={() => openDelete(r)}
      title="Delete"
      className="adminNotif-actionBtn"
    >
      <FaTrash />
    </Button>
  </div>
</td>

                    </tr>
                  ))}
                </tbody>
              </Table>

              <Pagination className="mb-2">
                <Pagination.First onClick={() => handlePageChange(1)} disabled={meta.page === 1} />
                <Pagination.Prev onClick={() => handlePageChange(meta.page - 1)} disabled={meta.page === 1} />
                {getPageItems(meta.totalPages, meta.page).map((item, i) =>
                  item === "start-ellipsis" || item === "end-ellipsis" ? (
                    <Pagination.Ellipsis key={`${item}-${i}`} disabled />
                  ) : (
                    <Pagination.Item
                      key={item}
                      active={item === meta.page}
                      onClick={() => handlePageChange(item)}
                    >
                      {item}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next
                  onClick={() => handlePageChange(meta.page + 1)}
                  disabled={meta.page === meta.totalPages}
                />
                <Pagination.Last
                  onClick={() => handlePageChange(meta.totalPages)}
                  disabled={meta.page === meta.totalPages}
                />
              </Pagination>

              <div className="text-muted">
                Showing {(meta.page - 1) * meta.limit + 1} to {Math.min(meta.page * meta.limit, meta.total)} of {meta.total}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* View modal */}
      <Modal show={showView} onHide={closeView} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Notification Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected ? (
            <>
              <Row className="g-3">
                <Col md={6}>
                  <div><b>ID:</b> {selected.Id}</div>
                  <div><b>Recipient:</b> {selected.recipientType} — {selected.recipientId}</div>
                  <div><b>Status:</b> {selected.isRead ? "Read" : "Unread"}</div>
                </Col>
                <Col md={6}>
                  <div><b>Order ID:</b> {selected.orderId || "N/A"}</div>
                  <div><b>Order Number:</b> {selected.orderNumber || "N/A"}</div>
                  <div><b>Created:</b> {formatDateTime(selected.created_at)}</div>
                </Col>
              </Row>

              <hr />

              <div><b>Type:</b> {selected.type || "N/A"}</div>
              <div className="mt-2"><b>Message:</b></div>
              <div className="adminNotif-messageBox">{selected.message || "-"}</div>
            </>
          ) : (
            <div className="text-muted">No data</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeView}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Delete modal */}
      <Modal show={showDelete} onHide={closeDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this notification?</p>
          <div className="text-muted">
            <div><b>ID:</b> {deleteTarget?.Id || "N/A"}</div>
            <div><b>Type:</b> {deleteTarget?.type || "N/A"}</div>
            <div><b>Recipient:</b> {deleteTarget?.recipientType} — {deleteTarget?.recipientId}</div>
          </div>
          <Alert variant="warning" className="mt-3 mb-0">This action cannot be undone.</Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDelete} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Yes, Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminNotifications;
